import { useEffect, useRef, useState } from "react";

// Shared keyboard/scroll lifecycle for quick views and mobile filter drawers.
export function useModalDialog(open, onClose, mediaQuery = null) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const [isModal, setIsModal] = useState(
    () => !mediaQuery || window.matchMedia(mediaQuery).matches
  );

  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!mediaQuery) return;
    const query = window.matchMedia(mediaQuery);
    const update = () => setIsModal(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [mediaQuery]);

  useEffect(() => {
    if (!open || !isModal || !dialogRef.current) return;
    const dialog = dialogRef.current;
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const focusable = () => [...dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== "hidden");

    document.body.style.overflow = "hidden";
    (focusable()[0] || dialog).focus({ preventScroll: true });

    const onKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
      } else if (event.key === "Tab") {
        const elements = focusable();
        const first = elements[0], last = elements[elements.length - 1];
        if (!first) {
          event.preventDefault();
          dialog.focus();
        } else if (!dialog.contains(document.activeElement) || document.activeElement === dialog) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open, isModal]);

  return { dialogRef, isModal };
}
